import { Trans, useLingui } from "@lingui/macro";
import { Globe, Lock, Users } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import { cn } from "@app/lib/utils";
import { useScope, type DataScope } from "@app/zustand/scope";

/**
 * Segmented control choosing the data perimeter (see {@link useScope}).
 *
 * "Mes patients" is nominative and allows patient drill-down; "Toute la
 * spécialité" is anonymised (a lock badge makes that explicit) and the backend
 * k-anonymises the payload. Masking follows from the perimeter, so this single
 * control is the source of truth for both the query scope and the anonymisation.
 */
export function ScopeSelector({ className }: { className?: string }) {
  const scope = useScope((s) => s.scope);
  const setScope = useScope((s) => s.setScope);
  const reuseAuthorised = useScope((s) => s.reuseAuthorised);
  const { t } = useLingui();

  // A speciality the warehouse does not cover has one perimeter: the reader's own patients.
  if (!reuseAuthorised) return null;

  const option = (value: DataScope, label: ReactNode, Icon: LucideIcon) => {
    const active = scope === value;
    return (
      <button
        type="button"
        role="radio"
        aria-checked={active}
        onClick={() => setScope(value)}
        className={cn(
          "flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-all",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
          active
            ? "bg-white text-primary shadow-sm"
            : "text-slate-500 hover:text-slate-700",
        )}
      >
        <Icon className="h-3.5 w-3.5 shrink-0" />
        {label}
      </button>
    );
  };

  return (
    <div className={cn("inline-flex items-center gap-2", className)}>
      <div
        role="radiogroup"
        aria-label={t`Périmètre de consultation`}
        className="inline-flex items-center gap-1 rounded-xl border border-slate-200 bg-slate-50 p-1"
      >
        {option("mine", <Trans>Mes patients</Trans>, Users)}
        {option("specialite", <Trans>Toute la spécialité</Trans>, Globe)}
      </div>
      {scope === "specialite" && <AnonymisedBadge />}
    </div>
  );
}

/** Small lock chip signalling that the current view is anonymised. */
export function AnonymisedBadge() {
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-primary/20 bg-primary/5 px-2 py-0.5 text-2xs font-semibold text-primary">
      <Lock className="h-3 w-3 shrink-0" />
      <Trans>Anonymisé</Trans>
    </span>
  );
}

/**
 * Whole-cohort k-anonymity guard: shown when a speciality-wide query matches
 * fewer than ``threshold`` patients and the backend has suppressed all detail.
 */
export function KAnonymityGuard({ threshold }: { threshold: number }) {
  return (
    <div className="mx-6 my-6 flex items-start gap-4 rounded-xl border border-slate-200 bg-slate-50 p-6">
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-slate-300 bg-white text-slate-500">
        <Lock className="h-5 w-5" />
      </div>
      <div>
        <h3 className="text-base font-semibold text-slate-900">
          <Trans>Trop peu de patients pour un affichage anonymisé</Trans>
        </h3>
        <p className="mt-1.5 max-w-2xl text-sm leading-relaxed text-slate-600">
          <Trans>
            Moins de {threshold} patients correspondent à cette recherche. Le
            détail est masqué pour éviter toute ré-identification à partir de
            critères rares. Élargissez la requête ou retirez un critère.
          </Trans>
        </p>
      </div>
    </div>
  );
}
